import React from 'react';
import { AuthContextUser } from '@readcloud/common';
import { ScrollIntoViewDiv } from '@readcloud/component-library';
import { Book, User } from '@readcloud/data';
import { HomeSettings } from '@readcloud/state';
import { default as classNames, default as classnames } from 'classnames';
import { useCloudsAndContentActions } from '../context/ActionsContext';
import { CloudWithBooks } from '../types';
import './BookList.scss';
import { CloudHeading } from './CloudHeading';
import { Tile } from './Tile';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Grab, Grip, GripVertical, Settings } from 'lucide-react';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';

export interface CloudBookListProps {
  cloud: CloudWithBooks;
  settings: HomeSettings;
  currentUserId: string;
  user: AuthContextUser;
  selectedCloudId?: string;
  knownUsers: User[];
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  toggleArrangingContent: (
    event: React.MouseEvent<HTMLDivElement>,
    cloudId?: string
  ) => void;
  arrangingCloudId?: string;
  setBookSettingsOpen: (bookId: string | null) => void;
  myContentBookIds:{
    contents: string[];
    purchases: string[];
  };
}

interface SortableTileProps {
  book: Book;
  cloud: CloudWithBooks;
  hideTitle: boolean;
  size: 'small' | 'medium' | 'large' | undefined;
  className?: string;
}

const SortableTile = ({
  book,
  cloud,
  hideTitle,
  size,
  className,
}: SortableTileProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: cloud.id + '-' + book.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={classNames('rc-cursor-grab active:rc-cursor-grabbing')}
    >
      <div className="rc-relative">
        <div className="rc-absolute rc-top-1/2 rc-left-1/2 rc-transform rc--translate-x-1/2 rc--translate-y-1/2 rc-z-10">
          <GripVertical className="rc-w-8 rc-h-8 rc-text-foreground" />
        </div>
        <Tile
          canRemoveFromCloud={false}
          hideTitle={hideTitle}
          size={size}
          book={book}
          className={className}
        />
      </div>
    </div>
  );
};

export const CloudBookList = (props: CloudBookListProps) => {
  const actions = useCloudsAndContentActions();
  const listLayout = props.settings.layout === 'list';
  const { cloud } = props;
  const [inContents, setInContents] = React.useState<boolean>(true);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 0,
        tolerance: 5, // Small movement tolerance before activating drag
      },
    }),
    useSensor(KeyboardSensor)
  );

  const [isDragging, setIsDragging] = React.useState(false);

  const handleDragStart = () => {
    setIsDragging(true);
    // Force stop any ongoing scroll momentum
    if (props.scrollContainerRef.current) {
      props.scrollContainerRef.current.style.overflow = 'hidden';
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    if (props.scrollContainerRef.current) {
      props.scrollContainerRef.current.style.overflow = 'auto';
    }

    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;
    const cloudId = activeId.split('-')[0];
    if (cloudId !== cloud.id) return;

    const oldIndex = cloud.bookData.findIndex(
      (book) => cloudId + '-' + book.id === activeId
    );
    const newIndex = cloud.bookData.findIndex(
      (book) => cloudId + '-' + book.id === overId
    );

    if (oldIndex !== newIndex) {
      const newBookData = arrayMove([...cloud.bookData], oldIndex, newIndex);
      cloud.bookData = newBookData;
      actions.updateCloud({
        id: cloud.id,
        books: newBookData.map((book) => book.id),
        customBookSortOrder: true,
      });
    }
  };

  return (
    <ScrollIntoViewDiv
      data-testid="cloud"
      scroll={
        cloud.id === props.selectedCloudId ||
        (!!props.arrangingCloudId && cloud.id === props.arrangingCloudId)
      }
      key={cloud.id}
      id={cloud.id}
      scrollContainerRef={props.scrollContainerRef}
      className={classNames(
        props.arrangingCloudId && props.arrangingCloudId === cloud.id
          ? 'rc-fixed rc-inset-x-0 rc-z-[100] rc-max-h-screen rc-overflow-y-auto rc-px-6'
          : '-rc-mx-6',
        isDragging && '!rc-touch-none rc-user-select-none'
      )}
      onClick={(event) =>
        props.toggleArrangingContent(event, props.arrangingCloudId)
      }
      style={
        props.arrangingCloudId && props.arrangingCloudId === cloud.id
          ? {
              top: '64px', // Height of the TopNavBar
              bottom: 0,
            }
          : undefined
      }
    >
      <div
        className={classNames(
          props.arrangingCloudId &&
            cloud.id === props.arrangingCloudId &&
            '-rc-mx-6',
          cloud.id === 'my-content' &&
            'rc-border-t-2 rc-border-border rc-bg-secondary rc-mt-6'
        )}
      >
        <CloudHeading
          canAddContent={
            (cloud.permissions?.canAddContent &&
              (props.user.role === 'Admin' ||
                props.user.role === 'ResellerAdmin' ||
                props.user.role === 'SchoolAdmin' ||
                (!cloud.addBookAdminOnly && props.user.role !== 'Student') ||
                cloud.admins.includes(props.currentUserId))) ??
            false
          }
          canDeleteCloud={cloud.permissions?.canDelete ?? false}
          canUpdateCloud={cloud.permissions?.canEdit ?? false}
          onRefresh={actions.refresh}
          knownUsers={props.knownUsers}
          onDeleteCloud={actions.deleteCloud}
          onUpdateCloud={actions.updateCloud}
          cloud={cloud}
          currentUserId={props.currentUserId}
          moduleAllowed={props.user.permissions}
          toggleArrangingContent={(event: React.MouseEvent<HTMLElement>) =>
            props.toggleArrangingContent(
              event as React.MouseEvent<HTMLDivElement>,
              cloud.id
            )
          }
          arrangingCloudId={props.arrangingCloudId}
          toggleVisibleMyContent={() => setInContents((prev) => !prev)}
          myContentVisible={inContents}
        />
        <div className="rc-h-full">
          {cloud.id !== 'my-content' ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              modifiers={listLayout ? [restrictToHorizontalAxis] : undefined}
            >
              {props.arrangingCloudId && props.arrangingCloudId === cloud.id ? (
                <SortableContext
                  items={cloud.bookData.map((book) => cloud.id + '-' + book.id)}
                  strategy={
                    listLayout
                      ? horizontalListSortingStrategy
                      : rectSortingStrategy
                  }
                >
                  <div
                    id={cloud.id + '-inner-container'}
                    className={classnames(
                      listLayout
                        ? 'rc-overflow-x-auto rc-overflow-y-hidden'
                        : 'rc-flex-wrap',
                      'rc-flex rc-gap-3 rc-px-6',
                      'lg:rc-gap-5',
                      isDragging && 'rc-touch-none rc-user-select-none'
                    )}
                  >
                    {cloud.bookData.map((book) => (
                      <SortableTile
                        key={cloud.id + book.id}
                        cloud={cloud}
                        book={book}
                        hideTitle={props.settings.hideTitle}
                        size={props.settings.tileSize}
                      />
                    ))}
                  </div>
                </SortableContext>
              ) : (
                <div
                  id={cloud.id + '-inner-container'}
                  className={classnames(
                    listLayout ? 'rc-overflow-x-auto' : 'rc-flex-wrap',
                    'rc-flex rc-gap-3 rc-px-6',
                    'lg:rc-gap-5'
                  )}
                >
                    {(inContents && cloud.name === "My Purchases"
                      ? cloud.bookData.filter((b) => !props.myContentBookIds.purchases.includes(b.id))
                      : cloud.bookData
                    ).map((book) => (
                    <div key={cloud.id + book.id} className="rc-cursor-pointer">
                      <Tile
                        key={cloud.id + book.id}
                        book={book}
                        canRemoveFromCloud={
                          cloud.permissions?.canRemoveContent ?? false
                        }
                        onRemoveFromCloud={() =>
                          actions.removeBookFromCloud({
                            cloudId: cloud.id,
                            book: book,
                          })
                        }
                        onClick={() =>
                          Promise.resolve(actions.tileClick(book, cloud))
                        }
                        hideTitle={props.settings.hideTitle}
                        size={props.settings.tileSize}
                        onRefresh={actions.refresh}
                        onContentSheetClick={() => props.setBookSettingsOpen(book.id)}
                        isMyContent={cloud.name !== "My Purchases"  && props.myContentBookIds.contents.includes(book.id)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </DndContext>
          ) : (
            <div
              id={cloud.id + '-inner-container'}
              className={classnames(
                listLayout ? 'rc-overflow-x-auto' : 'rc-flex-wrap',
                'rc-flex rc-gap-3 rc-px-6 ',
                // Bigger gaps on larger devices
                'lg:rc-gap-5'
                /* This padding + negative margin makes the scrollable list "pop-out" of it's paren's padding for a nice visual effect;
          to make it feel like there are more books outside of the page
          Note: it relies on the parent container to have padding of the same size, or, overflow-hidden, so that no extra scroll bars show
          */
              )}
            >
              { 
                  (inContents
                    ? cloud.bookData.filter((b) => !props.myContentBookIds.contents.includes(b.id))
                    : cloud.bookData
                  ).map((book) => (
                <div
                  key={cloud.id + book.id}
                  className="rc-cursor-pointer rc-relative"
                >
                  <div className="rc-absolute rc-top-2 rc-right-2 rc-bg-background/90 rc-rounded-full rc-shadow rc-p-1 rc-z-10 rc-pointer-events-none">
                    <Settings className="rc-w-4 rc-h-4 rc-text-primary" />
                  </div>
                  <Tile
                    canRemoveFromCloud={
                      cloud.permissions?.canRemoveContent ?? false
                    }
                    onRemoveFromCloud={() =>
                      actions.removeBookFromCloud({
                        cloudId: cloud.id,
                        book: book,
                      })
                    }
                    book={book}
                    onClick={() => props.setBookSettingsOpen(book.id)}
                    hideTitle={props.settings.hideTitle}
                    size={props.settings.tileSize}
                  />
                </div>
                ))
              }
            </div>
          )}
        </div>
      </div>
    </ScrollIntoViewDiv>
  );
};

export interface BookListProps {
  books: Book[];
  clouds: CloudWithBooks[];
  settings: HomeSettings;
  currentUserId: string;
  user: AuthContextUser;
  selectedCloudId?: string;
  knownUsers: User[];
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  toggleArrangingContent: (
    event: React.MouseEvent<HTMLDivElement>,
    cloudId?: string
  ) => void;
  arrangingCloudId?: string;
  setBookSettingsOpen: (bookId: string | null) => void;
}

export const BookList = (props: BookListProps) => {
  const [isBackdropVisible, setIsBackdropVisible] = React.useState(false);
  const booksInPurchasesOrContent = React.useMemo(() => {
    const myPurchasesCloud = props.clouds.find((c) => c.name === 'My Purchases');
    const myContentCloud = props.clouds.find((c) => c.name === 'My Content');
    const myPurchasesBookIds = new Set(myPurchasesCloud?.bookData?.map((b) => b.id) ?? []);
    const myContentBookIds = new Set(myContentCloud?.bookData?.map((b) => b.id) ?? []);
    const allBookIds = props.clouds
      .map((cloud) => cloud.name !== "My Purchases" && cloud.name !== "My Content" ? cloud.bookData?.map((b) => b.id) : [])
      .flat()

    return {
      contents: allBookIds.filter((b) => myContentBookIds.has(b)),
      purchases: allBookIds.filter((b) => myPurchasesBookIds.has(b))
    }
      
      
  }, [props.clouds, props.books]);

  React.useEffect(() => {
    if (props.arrangingCloudId) {
      setIsBackdropVisible(true);
    } else {
      // Keep the backdrop in DOM during fade out
      const timer = setTimeout(() => {
        setIsBackdropVisible(false);
      }, 500); // Match the transition duration
      return () => clearTimeout(timer);
    }
  }, [props.arrangingCloudId]);

  return (
    <div
      className={classNames(
        'rc-relative rc-flex rc-flex-col rc-px-6 rc-pb-20',
        props.settings.layout === 'list' ? 'rc-gap-2' : 'rc-gap-4',
        props.arrangingCloudId && 'rc-overflow-hidden'
      )}
    >
      {isBackdropVisible && (
        <div
          className={classNames(
            'rc-fixed rc-inset-0 rc-bg-black/80 rc-z-[99]',
            props.arrangingCloudId
              ? 'rc-motion-opacity-in-0'
              : 'rc-motion-opacity-out-0'
          )}
          onClick={(event) =>
            props.toggleArrangingContent(event, props.arrangingCloudId)
          }
        />
      )}
      {props.clouds?.map((cloud) => (
        <CloudBookList
          key={cloud.id}
          cloud={cloud}
          settings={props.settings}
          currentUserId={props.currentUserId}
          user={props.user}
          selectedCloudId={props.selectedCloudId}
          knownUsers={props.knownUsers}
          scrollContainerRef={props.scrollContainerRef}
          arrangingCloudId={props.arrangingCloudId}
          toggleArrangingContent={props.toggleArrangingContent}
          setBookSettingsOpen={props.setBookSettingsOpen}
          myContentBookIds={booksInPurchasesOrContent}
        />
      ))}
    </div>
  );
};
